import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { intelligenceMemory, type intelligencePersonalization } from '@cwos/api-client';
import { ErrorPanel, Pill, Refused, SkeletonSet, SuccessEmpty } from '@cwos/ui-admin';
import { SettingsCard, SettingsHeader } from './common.js';
import { LearnedPreferences } from './LearnedPreferences.js';

/**
 * WHAT PHIBIAN REMEMBERS ABOUT YOU — the see-and-control half of a capability
 * whose backend has existed since migration 1842000000000 with no screen.
 *
 * ---------------------------------------------------------------------------
 * WHY A PERSONALIZATION SCREEN IS A PRIVACY SCREEN
 * ---------------------------------------------------------------------------
 * A system that personalizes from a memory nobody can read or delete is the
 * thing this capability exists to prevent. So the screen owes three things, and
 * the third is the one usually skipped:
 *
 *   SEE       — what is remembered, in the reader's own terms.
 *   CONTROL   — remove it, with the effect legible afterwards.
 *   PROVENANCE— where each remembered thing CAME FROM. "Phibian remembers you
 *               prefer morning shifts" means something entirely different
 *               depending on whether you said it or it was inferred, and a
 *               person who cannot tell those apart cannot sensibly decide what
 *               to remove. `originSentence` is that answer, and it says
 *               "unclear" rather than guessing when the record's own fields
 *               disagree.
 *
 * ---------------------------------------------------------------------------
 * IT IS YOUR OWN RECORD, AND THERE IS NO PARAMETER THAT COULD MAKE IT SOMEBODY
 * ELSE'S
 * ---------------------------------------------------------------------------
 * This screen lives in Settings, where almost everything else is an
 * ORGANIZATION-WIDE administrative control gated on a permission key. This one
 * is not, and the difference matters more than its placement suggests. The two
 * routes behind it take no subject: the person whose memory is shown is the
 * signed-in caller, decided server-side from the session. An administrator
 * opening this sees THEIR OWN record and nothing else — not because the client
 * chose not to ask about anybody else, but because there is no shape of request
 * that asks.
 *
 * AN ADMINISTRATOR READING A WORKER'S MEMORY IS A DIFFERENT ACT. It needs its
 * own permission key, its own audit action and a reason recorded against the
 * access, and it deliberately does not exist. This screen must never grow a
 * person picker: the moment it does, every sentence above stops being true and
 * nothing in the screen's shape says so. The copy tells the reader that, because
 * an administrator standing in front of a screen called "what Phibian remembers"
 * will otherwise assume it can answer for their staff.
 *
 * ---------------------------------------------------------------------------
 * THE STATES THIS SCREEN KEEPS APART
 * ---------------------------------------------------------------------------
 * "We remember nothing about you" is the single most damaging wrong sentence
 * this product could render, so it is reachable from exactly one place: an
 * answered read with an empty record list. Everything else has its own words —
 *
 *   in flight        a skeleton. Claims nothing.
 *   401              your sign-in ended. Nothing about the record changed.
 *   403              an ANSWER, and an alarming one on a door that charges no
 *                    key. Rendered as a refusal with no retry, never as empty.
 *   unreachable      a fault. "Try again" is honest, and the copy says
 *                    explicitly not to read it as an empty record.
 *   misunderstood    a 2xx this app could not parse. NO RETRY — the server will
 *                    answer identically forever, and a retry button here would
 *                    be a lie with a button on it.
 *   absent           nothing is recorded. The one true empty.
 *   held, none in use  a MEASURED ZERO, which is a different sentence again:
 *                    records exist and none reaches Phibian's answers.
 *
 * RETAINED IS NOT IN USE, and the row-level copy is built on that. A revoked or
 * expired record is still listed — hiding it would say "we hold nothing" while
 * holding something — with `intelligenceUsesThis` as the field that tells the
 * truth about it.
 */

type Record_ = intelligencePersonalization.SubjectMemoryRecord;

const MEMORY_QUERY_KEY = ['intelligence', 'memory', 'about-me'] as const;

/** Retention and sensitivity in the reader's words rather than the enum's. */
const RETENTION_WORDS: Record<Record_['retentionClass'], string> = {
  SESSION_MEMORY: 'Kept for one conversation',
  WORKING_MEMORY: 'Kept for a few weeks',
  DURABLE_WORK_MEMORY: 'Kept while it stays relevant to your work',
  ORGANIZATION_MEMORY: 'Kept as organizational context',
  OUTCOME_MEMORY: 'Kept as a record of what happened',
};

function originTone(origin: intelligenceMemory.MemoryOrigin): 'plain' | 'warn' {
  // An inferred record, and one whose provenance contradicts itself, are the
  // two a person is most likely to want to dispute. They are marked, not
  // hidden and not sorted away.
  return origin === 'inferred' || origin === 'unclear' ? 'warn' : 'plain';
}

export function MemoryAboutMe() {
  const qc = useQueryClient();
  const query = useQuery({
    queryKey: MEMORY_QUERY_KEY,
    queryFn: () => intelligenceMemory.aboutMe(),
  });

  /** The id whose removal is awaiting a second press. One at a time. */
  const [confirming, setConfirming] = useState<string | null>(null);

  const forget = useMutation({
    mutationFn: (memoryId: string) => intelligenceMemory.revoke(memoryId),
    onSuccess: () => {
      setConfirming(null);
      // THE EFFECT MUST BE LEGIBLE AFTERWARDS. The list is re-read rather than
      // patched locally: the server is the authority on `intelligenceUsesThis`,
      // and a screen that flipped its own copy of the flag would be asserting
      // an outcome it did not observe. The reread is also what proves, on the
      // screen, the property the API guarantees — the record is still listed,
      // and it now says it is not in use.
      void qc.invalidateQueries({ queryKey: MEMORY_QUERY_KEY });
    },
  });

  if (query.isLoading) return <SkeletonSet surface="table" rows={4} />;

  if (query.isError && !query.data) {
    const failure = intelligenceMemory.memoryReadFailure(query.error);
    const sentence = intelligenceMemory.readFailureSentence(failure);
    return (
      <div className="rn-col" style={{ maxWidth: 760 }}>
        <SettingsHeader title="What Phibian remembers about you" />
        {failure.state === 'refused' ? (
          <Refused heading="Phibian would not answer" message={sentence} />
        ) : failure.state === 'unreadable' ? (
          <ErrorPanel message={sentence} onRetry={() => void query.refetch()} />
        ) : (
          // 401 and a body we could not parse. NO RETRY on either: one needs a
          // sign-in and the other needs a release, and a button that repeats
          // the request cannot produce either.
          <ErrorPanel message={sentence} />
        )}
      </div>
    );
  }

  const response = query.data!;
  const now = Date.now();
  const holding = intelligenceMemory.memoryHolding(response, now);
  const views = response.records.map((record) => intelligenceMemory.memoryRecordView(record, now));

  return (
    <div className="rn-col" style={{ maxWidth: 760 }}>
      <SettingsHeader
        title="What Phibian remembers about you"
        blurb="Everything Phibian has recorded about you personally, where each record came from, and whether it is still shaping the answers you get."
      />

      <SettingsCard>
        <h2 className="rn-card-title">Your record</h2>
        <p style={{ marginTop: 'var(--space-6)' }} data-testid="memory-holding">
          {intelligenceMemory.holdingSentence(holding)}
        </p>
        <p className="rn-muted" style={{ marginTop: 'var(--space-8)' }}>
          {intelligenceMemory.MEMORY_SCOPE_NOTE}
        </p>
        {/*
          SAID ON THE ADMIN SURFACE ESPECIALLY. An administrator reading a
          screen titled "what Phibian remembers" will assume it answers for
          their staff. It does not, it cannot be made to, and leaving that
          unsaid invites somebody to add the parameter that would break every
          authorization argument the route rests on.
        */}
        <p className="rn-muted" style={{ marginTop: 'var(--space-8)' }}>
          This is your own record. Phibian has no screen for reading another person’s memory, and
          this one cannot be pointed at anybody else — reading somebody else’s record would be a
          separate capability, with its own permission and its own audit trail.
        </p>
      </SettingsCard>

      <LearnedPreferences records={response.records} queryKey={MEMORY_QUERY_KEY} />

      {holding.kind === 'absent' ? (
        <SettingsCard>
          <SuccessEmpty
            heading="Nothing is recorded"
            message={intelligenceMemory.holdingSentence(holding)}
          />
        </SettingsCard>
      ) : (
        <>
          {holding.countIsFloor ? (
            <p className="rn-muted" role="note" data-testid="memory-count-is-floor">
              This page is full, so there may be more records than are listed here. The number above
              is a floor, not a total.
            </p>
          ) : null}
          {views.map((view) => (
            <MemoryRow
              key={view.record.memoryId}
              view={view}
              confirming={confirming === view.record.memoryId}
              busy={forget.isPending && forget.variables === view.record.memoryId}
              failed={
                forget.isError && forget.variables === view.record.memoryId ? forget.error : null
              }
              onAsk={() => setConfirming(view.record.memoryId)}
              onCancel={() => setConfirming(null)}
              onConfirm={() => forget.mutate(view.record.memoryId)}
            />
          ))}
        </>
      )}

      <SettingsCard>
        <h2 className="rn-card-title">{intelligenceMemory.MEMORY_CORRECTION_HEADING}</h2>
        {/*
          THE SERVER'S OWN WORDS. `correction.detail` is sent in the response
          expressly so a screen can answer this rather than leaving a person to
          guess, and restating it here in different words is how the two come to
          disagree.
        */}
        <p className="rn-muted" style={{ marginTop: 'var(--space-6)' }}>
          {response.correction.detail}
        </p>
        <p className="rn-muted" style={{ marginTop: 'var(--space-8)' }}>
          {intelligenceMemory.MEMORY_REVOKE_MEANS}
        </p>
      </SettingsCard>
    </div>
  );
}

function MemoryRow({
  view,
  confirming,
  busy,
  failed,
  onAsk,
  onCancel,
  onConfirm,
}: {
  view: intelligenceMemory.MemoryRecordView;
  confirming: boolean;
  busy: boolean;
  failed: unknown;
  onAsk: () => void;
  onCancel: () => void;
  onConfirm: () => void;
}) {
  const record = view.record;
  const labels = record.payload.labels ?? [];
  return (
    <SettingsCard>
      <div style={{ display: 'flex', gap: 'var(--space-8)', alignItems: 'baseline' }}>
        <h3 className="rn-card-title" style={{ margin: 0 }}>
          {record.payload.kind}
        </h3>
        {/*
          IN USE IS THE FIELD A PERSON ACTUALLY WANTS, so it is the badge. It is
          the SERVER's computed answer, not a re-derivation: see memoryUseState.
        */}
        <Pill variant={view.use === 'in-use' ? 'sky' : 'plain'} dot>
          {view.use === 'in-use' ? 'In use' : 'Not in use'}
        </Pill>
      </div>

      {record.payload.note ? (
        <p style={{ marginTop: 'var(--space-6)' }}>{record.payload.note}</p>
      ) : null}
      {labels.length > 0 ? (
        <p className="rn-muted" style={{ marginTop: 'var(--space-4)' }}>
          {labels.join(' · ')}
        </p>
      ) : null}

      <dl style={{ marginTop: 'var(--space-12)', display: 'grid', gap: 'var(--space-8)' }}>
        <div>
          <dt style={{ fontWeight: 600 }}>Where it came from</dt>
          <dd
            style={{ margin: 0 }}
            data-testid={`origin-${record.memoryId}`}
            data-tone={originTone(view.origin)}
          >
            {view.originSentence} <span className="rn-muted">Recorded by {record.source}.</span>
          </dd>
        </div>
        <div>
          <dt style={{ fontWeight: 600 }}>Is it shaping Phibian’s answers</dt>
          <dd style={{ margin: 0 }} data-testid={`use-${record.memoryId}`}>
            {view.useSentence}
          </dd>
        </div>
        <div>
          <dt style={{ fontWeight: 600 }}>How long it is kept</dt>
          <dd className="rn-muted" style={{ margin: 0 }}>
            {RETENTION_WORDS[record.retentionClass]}. {record.retentionPolicy}
          </dd>
        </div>
        {view.hasUnresolvedEvidence ? (
          <div>
            <dt style={{ fontWeight: 600 }}>What it points at</dt>
            {/*
              WITHHELD, NOT ABSENT. The response carries ids and hashes, never
              the evidence itself: resolving those is the evidence layer's
              question and its own permission's to answer. Rendering nothing
              here would say there is nothing behind the record.
            */}
            <dd className="rn-muted" style={{ margin: 0 }}>
              This record points at{' '}
              {record.provenanceRefs.length + (record.payload.refs ?? []).length} other record(s).
              Phibian does not release what they contain on this page — this page is about you, and
              those belong to whichever part of Phibian owns them.
            </dd>
          </div>
        ) : null}
      </dl>

      {view.revocable ? (
        confirming ? (
          <div className="rn-col" style={{ marginTop: 'var(--space-12)' }}>
            <p role="alert">{intelligenceMemory.MEMORY_REVOKE_MEANS}</p>
            <div style={{ display: 'flex', gap: 'var(--space-8)' }}>
              <button type="button" disabled={busy} onClick={onConfirm}>
                {busy ? 'Removing…' : 'Yes, stop using this'}
              </button>
              <button type="button" disabled={busy} onClick={onCancel}>
                Keep it
              </button>
            </div>
          </div>
        ) : (
          <button type="button" style={{ marginTop: 'var(--space-12)' }} onClick={onAsk}>
            Remove this
          </button>
        )
      ) : (
        // NO BUTTON ON A RECORD THAT IS ALREADY NOT IN USE. Revoking one would
        // change a label and nothing else — the physical purge has no caller in
        // the API — and a control that carries a value nothing reads is the
        // defect class this codebase names inert carriage.
        <p className="rn-muted" style={{ marginTop: 'var(--space-12)' }}>
          There is nothing to remove: this record is already not reaching Phibian’s answers.
        </p>
      )}

      {failed !== null ? (
        <p role="alert" style={{ marginTop: 'var(--space-8)' }} data-testid="forget-failed">
          {revokeFailureSentence(failed)}
        </p>
      ) : null}
    </SettingsCard>
  );
}

/**
 * WHY A FAILED REMOVAL NEEDS ITS OWN SENTENCES.
 *
 * A person who pressed "remove" and saw a generic apology does not know whether
 * the record is still feeding the model. Every branch below answers that
 * question, and the 409 branch is the one worth reading twice: "already
 * revoked" is not a failure at all from the reader's point of view — the thing
 * they wanted is true — and telling them it went wrong would send them looking
 * for a problem that does not exist.
 */
export function revokeFailureSentence(err: unknown): string {
  const status =
    typeof err === 'object' &&
    err !== null &&
    typeof (err as { status?: unknown }).status === 'number'
      ? (err as { status: number }).status
      : null;
  if (status === 409) {
    return 'This record had already been removed. It is not reaching Phibian’s answers.';
  }
  if (status === 404) {
    return 'Phibian has no such record about you. It may have been removed already, or the page may be out of date — reload to see the current record.';
  }
  if (status === 401) {
    return 'Your sign-in ended before this could be sent, so nothing was removed. Sign in again and try once more.';
  }
  return 'Phibian could not remove this just now, so it has NOT been removed and is still being used. Try again.';
}
